
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";

export const AdminButton = () => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Pour l'utilisateur avec ID 83b55862-d096-44b4-b0af-4f59e29d8297
  const hardcodedAdminId = "83b55862-d096-44b4-b0af-4f59e29d8297";

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Vérification directe pour l'utilisateur spécifique
          if (user.id === hardcodedAdminId) {
            setIsAdmin(true);
            return;
          }
          
          // Fallback à la vérification normale pour les autres utilisateurs
          try {
            const { data, error } = await supabase
              .from('user_roles')
              .select('*')
              .eq('user_id', user.id)
              .eq('role', 'admin')
              .maybeSingle();
            
            if (error) {
              // Si c'est l'erreur de récursion, on ignore silencieusement
              if (error.message?.includes('infinite recursion')) {
                console.log('Ignoring recursion error, using alternative method');
              } else {
                throw error;
              }
            } else {
              setIsAdmin(!!data);
            }
          } catch (innerError) {
            console.warn('Fallback method also failed:', innerError);
          }
        }
      } catch (error) {
        console.error('Erreur de vérification du statut admin', error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  const handleAdminAccess = () => {
    if (isMobile) {
      // For mobile, use direct location change
      window.location.href = '/admin/users';
    } else {
      // For desktop, use navigate to avoid page reload
      navigate('/admin/users');
    }
  };

  // Ne retourne rien pendant le chargement
  if (loading) return null;

  // Affiche le bouton uniquement si l'utilisateur est admin
  return isAdmin ? (
    <Button 
      onClick={handleAdminAccess} 
      variant="secondary"
      className="flex items-center gap-2"
    >
      <Shield className="h-4 w-4" />
      Gestion des utilisateurs
    </Button>
  ) : null;
};
